// src/hooks/useSingleFlight.js
import { useCallback, useRef } from 'react';

/**
 * Wraps an async handler so overlapping invocations are dropped.
 *
 * A `disabled={isLoading}` prop only guards once React has re-rendered, so any
 * handler that awaits before flipping its loading state stays clickable for the
 * whole round trip — rage clicks then fire it several times. This guard is a
 * ref, so it blocks from the very next click onward regardless of render timing.
 *
 * On a dropped call the event is defaultPrevented, otherwise a form would fall
 * through to a native submit (full page reload) while the first call is running.
 *
 * @param {Function} handler - sync or async handler; latest reference is always used
 * @returns {Function} guarded handler, resolving to undefined when dropped
 */
export const useSingleFlight = (handler) => {
    const inFlightRef = useRef(false);
    const handlerRef = useRef(handler);
    handlerRef.current = handler;

    return useCallback(async (...args) => {
        const event = args[0];
        if (inFlightRef.current) {
            if (typeof event?.preventDefault === 'function') event.preventDefault();
            return undefined;
        }

        inFlightRef.current = true;
        try {
            return await handlerRef.current(...args);
        } finally {
            inFlightRef.current = false;
        }
    }, []);
};

export default useSingleFlight;
